import { Link, useSearchParams } from "react-router-dom";
import { useResetPassword } from "../../hooks/auth/useResetPassword";
import {
  resetPasswordSchema,
  type ResetPasswordInput,
} from "../../utils/zod/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";
import { PasswordField } from "../../components/common/ui/passwordField";
import { ConstellationBackground } from "../../components/common/layout/constellationBackground";
import { ThemedLogo } from "../../components/common/ui/themedLogo";

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { mutate, isPending } = useResetPassword();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { newPassword: "", confirmPassword: "" },
  });

  if (!token || token.trim() === "") {
    return (
      <div className="min-h-screen flex flex-col bg-global-page relative isolate">
        <ConstellationBackground />
        <div className="flex-1 flex items-center justify-center px-3 sm:px-4 py-6 sm:py-8">
          <div className="w-full max-w-md text-center">
            <div className="bg-global-overlay backdrop-blur-sm border border-global-primary shadow-2xl rounded-xl p-6">
              <p className="text-global-muted mb-4">
                Falta el enlace de recuperación. Solicita uno nuevo desde el
                inicio de sesión.
              </p>
              <Link
                to="/auth/forgot-password"
                className="text-global-accent hover:underline"
              >
                Recuperar contraseña
              </Link>
              <span className="mx-2">|</span>
              <Link to="/auth" className="text-global-accent hover:underline">
                Volver al inicio de sesión
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const onSubmit = (data: ResetPasswordInput) => {
    mutate({ token: token.trim(), newPassword: data.newPassword });
  };

  return (
    <div className="min-h-screen flex flex-col bg-global-page relative isolate">
      <ConstellationBackground />
      <div className="flex-1 flex items-center justify-center px-3 sm:px-4 py-6 sm:py-8">
        <div className="w-full max-w-md">
          <div className="bg-global-overlay backdrop-blur-sm border border-global-primary shadow-2xl rounded-xl sm:rounded-2xl overflow-hidden">
            <div className="px-6 sm:px-8 pt-7 sm:pt-9 pb-5 sm:pb-6 text-center border-b border-global-primary">
              <ThemedLogo className="mx-auto h-12 w-auto" />
              <p className="text-[10px] text-global-muted tracking-[0.25em] uppercase mt-1">
                Software
              </p>
              <h2 className="mt-4 text-lg sm:text-xl font-semibold tracking-tight text-global-title">
                Nueva contraseña
              </h2>
              <p className="mt-1 text-xs sm:text-sm text-global-accent">
                Elige una contraseña segura de al menos 8 caracteres
              </p>
            </div>

            <div className="px-5 sm:px-6 md:px-8 py-5 sm:py-6 md:py-7">
              <form
                className="space-y-4 sm:space-y-5"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="space-y-1.5 sm:space-y-2">
                  <label className="block text-xs sm:text-sm font-medium text-global-label">
                    Nueva contraseña
                  </label>
                  <Controller
                    name="newPassword"
                    control={control}
                    render={({ field }) => (
                      <PasswordField
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="••••••••"
                      />
                    )}
                  />
                  {errors.newPassword && (
                    <p className="text-size-2xs text-global-error">
                      {errors.newPassword.message}
                    </p>
                  )}
                </div>

                <div className="space-y-1.5 sm:space-y-2">
                  <label className="block text-xs sm:text-sm font-medium text-global-label">
                    Confirmar contraseña
                  </label>
                  <Controller
                    name="confirmPassword"
                    control={control}
                    render={({ field }) => (
                      <PasswordField
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="••••••••"
                      />
                    )}
                  />
                  {errors.confirmPassword && (
                    <p className="text-size-2xs text-global-error">
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isPending}
                  className="mt-2 inline-flex w-full items-center justify-center rounded-lg btn-global-primary-gradient px-4 py-3 sm:py-2.5 text-sm sm:text-base font-bold shadow-global-button focus:outline-none focus:ring-2 focus-global-ring focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition touch-manipulation uppercase tracking-wide"
                >
                  {isPending ? (
                    <div className="flex items-center gap-2">
                      <svg
                        className="animate-spin h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span>Guardar nueva contraseña</span>
                    </div>
                  ) : (
                    "Guardar nueva contraseña"
                  )}
                </button>
              </form>

              <p className="mt-5 text-center">
                <Link
                  to="/auth"
                  className="text-sm text-global-accent hover:underline"
                >
                  Volver al inicio de sesión
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
